/**
 * Karta 3D (blok "Pieczątka i podpis" -> drawer). Kartę obraca się przeciągnięciem (mysz / palec) lub strzałkami.
 * Przeciąganie w poziomie obraca kartę, w pionie pochyla ją (przeciągnięcie w górę zawsze odchyla górę karty od
 * patrzącego – przy przodzie i przy tyle). Karta płynnie podąża za palcem z lekkim opóźnieniem, po puszczeniu
 * dociąga do najbliższej pozycji i wraca do braku pochylenia. Przy obrotach i animacjach sama lekko się pochyla.
 * Atrybuty (ustawiane w edytorze motywu):
 * - snap: "angles" (przód, tył i pozycje pod kątem), "faces" (tylko przód / tył) lub "free" (bez dociągania)
 * - snap-angle: kąt pozycji skośnych (stopnie), rest-tilt: lekki obrót przodu / tyłu w spoczynku (stopnie)
 * - max-tilt: zakres pochylenia góra–dół (stopnie)
 * - motion: ruch, gdy nikt nie dotyka karty – "none", "tilt" (naturalny dryf: kołysanie, pochylanie do góry i co jakiś
 *   czas odwrócenie na tył i z powrotem – w stronę, w którą karta jest akurat wychylona) lub "spin" (powolny obrót 360°)
 * - speed: tempo ruchu 1–10, angle: zakres kołysania (stopnie)
 * - glare: połysk zależny od kąta
 * - intro: przy otwarciu drawera karta wjeżdża tyłem i obraca się do przodu, układając się pod lekkim kątem i pochylona
 *   do góry; ruch w spoczynku zaczyna się płynnie od tej pozycji
 * Rozmiar karty jest dopasowywany automatycznie tak, by karta i jej cień przy żadnym ustawieniu nie wyszły poza obszar.
 * Animacja działa tylko, gdy karta jest widoczna (otwarty drawer) i zatrzymuje się, gdy karta jest w spoczynku.
 */
if (!customElements.get('card-3d')) {
  const IDLE_DELAY = 2500; // po tylu ms bez dotyku zaczyna się ruch w spoczynku
  const IDLE_FADE_IN = 3000; // ruch w spoczynku narasta stopniowo przez tyle ms (bez skoku)
  const DRAG_FOLLOW = 70; // ms – karta podąża za palcem z lekkim opóźnieniem (im więcej, tym większe)
  const DRAG_ROTATE = 0.45; // stopnie obrotu na piksel przeciągnięcia w poziomie
  const DRAG_TILT = 0.25; // stopnie pochylenia na piksel przeciągnięcia w pionie
  const MOTION_TILT = 3; // przy obrocie karta sama się pochyla: stopnie pochylenia na stopień obrotu na klatkę
  // Miękka, lekko nadtłumiona sprężyna (dociąganie bez odbicia). Liczona w czasie, krokami po SPRING_STEP ms, a nie
  // w klatkach: ten sam ruch przy 60 i 120 Hz, a zgubiona klatka nie zostawia karty w tyle (bez doganiania szarpnięciem)
  const SPRING_STIFFNESS = 40.5; // 1/s²
  const SPRING_DAMPING = 15; // 1/s
  const SPRING_STEP = 4; // ms
  const MOTION_TILT_SMOOTH = 250; // ms – pochylenie przy obrocie narasta i opada płynnie, bez skoków za prędkością
  const PERSPECTIVE_RATIO = 5; // perspektywa = 5 × szerokość karty – ten sam efekt 3D na każdym rozmiarze ekranu
  const INTRO_DURATION = 1400; // ms – obrót z tyłu do przodu przy otwarciu drawera
  const DRIFT_TILT = 0.75; // pochylenie w dryfie sięga do 3/4 zakresu (tylko do góry)

  // Cień pod kartą – dwie warstwy (ułamki szerokości karty: przesunięcie w dół, rozmycie, rozprzestrzenienie).
  // Ujemne rozprzestrzenienie chowa cień pod bokami karty: widać go tylko pod spodem, nigdy przy krawędzi obszaru.
  const SHADOWS = [
    { offset: 0.07, blur: 0.08, spread: -0.06 }, // miękki cień pod kartą
    { offset: 0.02, blur: 0.03, spread: -0.02 }, // cień przy samej krawędzi karty
  ];

  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const clamp = (value, limit) => Math.max(-limit, Math.min(limit, value));
  const smoothstep = (value) => value * value * (3 - 2 * value);
  const easeInOut = (value) => 0.5 - Math.cos(value * Math.PI) / 2;
  const easeOut = (value) => 1 - (1 - value) ** 3;
  // Ruch o najmniejszym szarpnięciu: prędkość i przyspieszenie zerowe na początku i na końcu
  const smootherstep = (value) => value * value * value * (value * (6 * value - 15) + 10);
  const overflowCache = new Map();

  // Pochylenie jest nadawane względem samej karty (stąd efekt karty "leżącej" pod kątem), ale skalowane przez cos(obrotu):
  // przy przodzie = pochylenie, bokiem = 0, przy tyle = –pochylenie. Dzięki temu na ekranie kierunek jest zawsze ten sam
  // i zmienia się płynnie (bez przeskoku przy obróceniu karty).
  function localTilt(rotateY, tilt) {
    return tilt * Math.cos(toRadians(rotateY));
  }

  // Siła cienia: pełna, gdy karta stoi przodem / tyłem, znika płynnie, gdy stoi bokiem (brak ostrej linii cienia)
  function shadowStrength(rotateY, tilt) {
    const cos = Math.cos(toRadians(rotateY));

    return cos * cos * Math.cos(toRadians(tilt));
  }

  // Najgorszy przypadek: o ile (jako ułamek szerokości karty) karta i jej cień mogą wyjść poza prostokąt karty –
  // w poziomie, w górę i w dół – dla wszystkich kątów obrotu i pochylenia (z grubością i perspektywą).
  // Zasięg cienia liczony z pełnym rozmyciem, więc przycięcie przy krawędzi obszaru nigdy nie jest widoczne.
  function maxOverflow(aspectRatio, thickness, maxTilt) {
    const key = `${aspectRatio.toFixed(3)}|${thickness.toFixed(3)}|${maxTilt}`;

    if (overflowCache.has(key)) {
      return overflowCache.get(key);
    }

    const halfWidth = 0.5;
    const halfHeight = 0.5 / aspectRatio;
    const halfDepth = thickness / 2;
    const reach = (shadow) => shadow.spread + shadow.blur;
    const shadowSide = halfWidth + Math.max(0, ...SHADOWS.map(reach));
    const shadowTop = -halfHeight - Math.max(0, ...SHADOWS.map((shadow) => reach(shadow) - shadow.offset));
    const shadowBottom = halfHeight + Math.max(0, ...SHADOWS.map((shadow) => reach(shadow) + shadow.offset));
    const cardCorners = [];
    const shadowCorners = [];

    for (const z of [-halfDepth, halfDepth]) {
      for (const x of [-1, 1]) {
        cardCorners.push([x * halfWidth, -halfHeight, z], [x * halfWidth, halfHeight, z]);
        shadowCorners.push([x * shadowSide, shadowTop, z], [x * shadowSide, shadowBottom, z]);
      }
    }

    let maxX = 0;
    let maxTop = 0;
    let maxBottom = 0;

    for (let rotateY = 0; rotateY < 360; rotateY += 2) {
      for (let tilt = -maxTilt; tilt <= maxTilt; tilt += Math.max(1, maxTilt / 6)) {
        const local = localTilt(rotateY, tilt);
        const sinY = Math.sin(toRadians(rotateY)), cosY = Math.cos(toRadians(rotateY));
        const sinX = Math.sin(toRadians(local)), cosX = Math.cos(toRadians(local));
        const corners = shadowStrength(rotateY, tilt) > 0.03 ? cardCorners.concat(shadowCorners) : cardCorners;

        for (const [x, y, z] of corners) {
          // rotateY(…) rotateX(…): najpierw pochylenie względem karty, potem obrót
          const y1 = y * cosX - z * sinX;
          const z1 = y * sinX + z * cosX;
          const x2 = x * cosY + z1 * sinY;
          const z2 = -x * sinY + z1 * cosY;
          const scale = PERSPECTIVE_RATIO / (PERSPECTIVE_RATIO - z2);

          maxX = Math.max(maxX, Math.abs(x2 * scale));
          maxTop = Math.max(maxTop, -y1 * scale);
          maxBottom = Math.max(maxBottom, y1 * scale);
        }
      }
    }

    const result = { x: maxX - halfWidth, top: maxTop - halfHeight, bottom: maxBottom - halfHeight };

    overflowCache.set(key, result);
    return result;
  }

  class Card3d extends HTMLElement {
    connectedCallback() {
      this.card = this.querySelector('.card-3d__card');

      if (!this.card) {
        return;
      }

      if (!this.initialized) {
        this.initialized = true;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.baseY = 0;
        this.restOffsetY = this.snap === 'free' ? 0 : this.restY;
        this.rotateY = this.restOffsetY;
        this.tilt = 0;
        this.velocityY = 0;
        this.tiltVelocity = 0;
        this.lastInteraction = 0;
        this.lastTime = 0;
        this.idleStart = 0;
        this.flipDirection = -1;
        this.tick = this.tick.bind(this);

        this.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.addEventListener('pointermove', this.onPointerMove.bind(this));
        this.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.addEventListener('pointercancel', this.onPointerUp.bind(this));
        this.addEventListener('keydown', this.onKeyDown.bind(this));
        this.addEventListener('dragstart', (event) => event.preventDefault());
      }

      // Otwarcie drawera (zdarzenie motywu, zanim drawer się pokaże): obrót z tyłu do przodu
      this.drawerAbort?.abort();
      this.drawerAbort = new AbortController();
      this.closest('x-drawer')?.addEventListener('dialog:before-show', () => this.startIntro(), { signal: this.drawerAbort.signal });

      // Drawer z "append-to-body" przenosi element w DOM, więc obserwatory są (re)startowane przy każdym podłączeniu
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;

        if (this.visible) {
          this.lastInteraction = performance.now() - IDLE_DELAY + 800;
          this.requestTick();
        }
      });

      this.observer.observe(this);
      this.resizeObserver = new ResizeObserver(() => this.fit());
      this.resizeObserver.observe(this);
      this.fit();
      this.render();
    }

    disconnectedCallback() {
      this.drawerAbort?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    get snap() {
      return this.getAttribute('snap') || 'angles';
    }

    get snapAngle() {
      return Math.min(45, Math.max(5, parseFloat(this.getAttribute('snap-angle')) || 30));
    }

    get maxTilt() {
      const value = parseFloat(this.getAttribute('max-tilt'));

      return Math.min(40, Math.max(0, Number.isNaN(value) ? 30 : value));
    }

    get restY() {
      return this.snap === 'free' ? 0 : parseFloat(this.getAttribute('rest-tilt')) || 0;
    }

    get motion() {
      return this.reducedMotion ? 'none' : this.getAttribute('motion') || 'none';
    }

    get speed() {
      return Math.min(10, Math.max(1, parseFloat(this.getAttribute('speed')) || 4));
    }

    get angle() {
      return Math.min(45, Math.max(0, parseFloat(this.getAttribute('angle')) || 20));
    }

    // Zakres pochylenia w dryfie (tylko do góry) i pochylenie, w którym karta kończy obrót przy otwarciu – to samo, od
    // którego zaczyna się dryf, więc przejście jest płynne
    get driftTilt() {
      return Math.min(this.maxTilt, this.angle) * DRIFT_TILT;
    }

    get introTilt() {
      if (this.motion === 'tilt') {
        return this.driftTilt;
      }

      return this.motion === 'spin' ? Math.min(this.maxTilt, this.angle) * 0.5 : Math.min(this.maxTilt, 8);
    }

    // Start obrotu przy otwarciu: karta stoi tyłem (ten sam obrót co w spoczynku, przesunięty o 180°), a animację
    // prowadzi tick() od pierwszej klatki, w której karta jest widoczna
    startIntro() {
      if (!this.hasAttribute('intro') || this.reducedMotion || !this.card) {
        return;
      }

      // Zawsze kończy przodem, także gdy przy poprzednim otwarciu została obrócona tyłem
      this.baseY = Math.round(this.baseY / 360) * 360;
      this.updateRestOffset();

      const toY = this.baseY + this.restOffsetY;

      this.intro = { fromY: toY - 180, toY, toTilt: this.introTilt, start: null };
      this.rotateY = this.intro.fromY;
      this.tilt = 0;
      this.velocityY = 0;
      this.tiltVelocity = 0;
      this.motionTilt = 0;
      this.flip = null;
      this.idleStart = 0;
      this.render();
      this.requestTick();
    }

    // Obrót z tyłu do przodu: szybki start, miękkie ułożenie; pochylenie narasta w trakcie i zostaje na końcu
    playIntro(time) {
      if (this.intro.start === null) {
        this.intro.start = time;
      }

      const progress = Math.min(1, (time - this.intro.start) / INTRO_DURATION);

      this.rotateY = this.intro.fromY + (this.intro.toY - this.intro.fromY) * easeOut(progress);
      this.tilt = this.intro.toTilt * smoothstep(Math.min(1, progress * 1.25));

      if (progress === 1) {
        // Dryf startuje od razu w pełni i od tej samej pozycji (kołysanie od zera, pochylenie od szczytu w dół)
        this.intro = null;
        this.velocityY = 0;
        this.tiltVelocity = 0;
        this.introHandoff = true;
        this.lastInteraction = time - IDLE_DELAY;
      }
    }

    // Pozycje, do których karta dociąga: przód / tył (co 180°) oraz – w trybie "angles" – pozycje pod kątem po obu stronach
    snapPositions(around) {
      const offsets = this.snap === 'angles' ? [-this.snapAngle, 0, this.snapAngle] : [0];
      const turn = Math.round(around / 180);
      const positions = [];

      for (let k = turn - 2; k <= turn + 2; k++) {
        for (const offset of offsets) {
          positions.push(k * 180 + offset);
        }
      }

      return positions.sort((a, b) => a - b);
    }

    isFace(position) {
      return Math.abs(position - Math.round(position / 180) * 180) < 0.01;
    }

    // Lekki obrót w spoczynku dotyczy tylko pozycji "na wprost" (przód / tył)
    updateRestOffset() {
      this.restOffsetY = this.snap !== 'free' && this.isFace(this.baseY) ? this.restY : 0;
    }

    onPointerDown(event) {
      if (event.button !== 0) {
        return;
      }

      this.intro = null; // dotknięcie przerywa obrót przy otwarciu – karta zostaje tam, gdzie jest
      this.dragging = true;
      this.dragY = this.rotateY;
      this.dragTilt = this.tilt;
      this.pointerX = event.clientX;
      this.pointerY = event.clientY;
      this.pointerTime = event.timeStamp;
      this.velocityY = 0;
      this.tiltVelocity = 0;
      this.classList.add('is-dragging');
      this.setPointerCapture(event.pointerId);
      this.lastInteraction = performance.now();
      this.requestTick();
    }

    // Palec wyznacza cel – samą kartę przesuwa tick(), płynnie i z lekkim opóźnieniem
    onPointerMove(event) {
      if (!this.dragging) {
        return;
      }

      this.dragY += (event.clientX - this.pointerX) * DRAG_ROTATE;
      this.dragTilt = clamp(this.dragTilt - (event.clientY - this.pointerY) * DRAG_TILT, this.maxTilt);
      this.pointerX = event.clientX;
      this.pointerY = event.clientY;
      this.pointerTime = event.timeStamp;
      this.lastInteraction = performance.now();
    }

    onPointerUp(event) {
      if (!this.dragging) {
        return;
      }

      this.dragging = false;
      this.classList.remove('is-dragging');

      if (this.hasPointerCapture(event.pointerId)) {
        this.releasePointerCapture(event.pointerId);
      }

      // Palec / mysz zatrzymane przed puszczeniem = brak rzutu
      if (event.timeStamp - this.pointerTime > 100) {
        this.velocityY = 0;
      }

      this.release(this.dragY);
    }

    onKeyDown(event) {
      const direction = { ArrowLeft: -1, ArrowRight: 1 }[event.key];

      if (direction || event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        this.intro = null;
      }

      if (direction) {
        event.preventDefault();

        if (this.snap === 'free') {
          this.baseY += direction * 30;
        } else {
          const positions = this.snapPositions(this.baseY);

          this.baseY = (direction > 0 ? positions.find((p) => p > this.baseY + 0.01) : positions.reverse().find((p) => p < this.baseY - 0.01)) ?? this.baseY;
        }

        this.updateRestOffset();
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        // Krótkie pochylenie – karta sama wraca do braku pochylenia
        event.preventDefault();
        this.tiltVelocity += event.key === 'ArrowUp' ? 3 : -3;
      } else {
        return;
      }

      this.lastInteraction = performance.now();
      this.requestTick();
    }

    // Po puszczeniu: cel liczony z miejsca, w którym karta "wyląduje" przy obecnej prędkości (rzut kartą). Rzut jest
    // ograniczony, więc karta zawsze dociąga do najbliższej pozycji, a nie kręci się kilka razy.
    release(fromY) {
      this.velocityY = clamp(this.velocityY, 20);

      const landingY = fromY + (this.reducedMotion ? 0 : clamp(this.velocityY * 10, 140));

      if (this.snap === 'free') {
        this.baseY = landingY;
      } else {
        // Najbliższa pozycja: przód / tył albo pozycja pod kątem (nigdy bokiem – kąt skosu to maks. 45°)
        this.baseY = this.snapPositions(landingY).reduce((best, p) => (Math.abs(p - landingY) < Math.abs(best - landingY) ? p : best));
      }

      this.updateRestOffset();

      if (this.reducedMotion) {
        this.velocityY = 0;
      }

      this.lastInteraction = performance.now();
      this.requestTick();
    }

    // Dobiera szerokość karty, odstępy i cień tak, by karta i cień nigdy nie wyszły poza obszar elementu
    fit() {
      const available = this.clientWidth;

      if (!available) {
        return;
      }

      const style = getComputedStyle(this);
      const aspectRatio = parseFloat(style.getPropertyValue('--card-3d-aspect-ratio')) || 1.586;
      const thickness = parseFloat(style.getPropertyValue('--card-3d-thickness')) || 0;
      const overflow = maxOverflow(aspectRatio, thickness / available, this.maxTilt);
      const width = Math.floor((available - 6) / (1 + 2 * overflow.x)); // + 3px zapasu z każdej strony
      const px = (fraction) => `${(fraction * width).toFixed(1)}px`;

      this.perspective = width * PERSPECTIVE_RATIO;
      this.style.setProperty('--card-3d-fit-width', `${width}px`);
      this.style.setProperty('--card-3d-padding-top', `${Math.ceil(overflow.top * width) + 3}px`);
      this.style.setProperty('--card-3d-padding-bottom', `${Math.ceil(overflow.bottom * width) + 3}px`);

      SHADOWS.forEach((shadow, index) => {
        this.style.setProperty(`--card-3d-shadow-${index + 1}`, `0 ${px(shadow.offset)} ${px(shadow.blur)} ${px(shadow.spread)}`);
      });

      this.render();
    }

    requestTick() {
      if (!this.frame) {
        this.frame = requestAnimationFrame(this.tick);
      }
    }

    // Ruch w spoczynku. Zaczyna się od miejsca, w którym karta stoi, i narasta stopniowo (bez skoku). Po obrocie przy
    // otwarciu drawera – od razu w pełni, bo karta stoi już w pozycji startowej dryfu.
    // Kołysanie to jedna gładka fala (bez małych zawróceń po drodze): karta przechodzi pełnym ruchem z jednej strony na
    // drugą i zwalnia tylko przy wychyleniu. Tempo (±25%) i zakres (85–100%) zmieniają się powoli i płynnie, więc ruch
    // jest naturalny, a każda zmiana tempa – miękka. Fazy są całkowane w czasie, więc zmiana tempa nie daje skoku.
    idleMotion(time, delta) {
      if (!this.idleStart) {
        this.idleStart = this.introHandoff ? time - IDLE_FADE_IN : time;
        this.introHandoff = false;
        this.swayPhase = 0;
        this.swayTempo = 0;
        this.tiltPhase = 0;
        this.tiltTempo = 0;
        this.rangePhase = 0;
        this.nextFlip = time + this.flipPause;
        this.flip = null;
      }

      const blend = smoothstep(Math.min(1, (time - this.idleStart) / IDLE_FADE_IN));
      const tiltRange = Math.min(this.maxTilt, this.angle);
      const frequency = 0.0001 * this.speed; // rad/ms

      this.swayTempo += delta * frequency * 0.23;
      this.tiltTempo += delta * frequency * 0.17;
      this.rangePhase += delta * frequency * 0.11;
      this.tiltPhase += delta * frequency * 0.8 * (1 + 0.2 * Math.sin(this.tiltTempo));

      if (this.motion === 'spin') {
        // Tempo 1–10: pełny obrót od ok. 140 s do 14 s; pochylenie tylko do góry i z powrotem do pionu
        this.baseY += (this.speed * 2.5 * delta * blend) / 1000;

        return { y: 0, tilt: blend * tiltRange * 0.5 * (0.5 + 0.5 * Math.cos(this.tiltPhase * 0.8)) };
      }

      // Kołysanie stoi w miejscu podczas odwracania – karta obraca się z wychylenia, a po odwróceniu kołysze się dalej
      // od tego samego miejsca (bez skoku)
      if (!this.flip) {
        const previous = this.swayPhase;

        this.swayPhase += delta * frequency * (1 + 0.25 * Math.sin(this.swayTempo));

        // Co jakiś czas karta odwraca się na tył, a po chwili wraca. Nie od razu: czeka na skrajne wychylenie (faza
        // przechodzi przez π/2 + kπ – karta zwalnia i zaraz by zawróciła) i stamtąd obraca się dalej w tę samą stronę.
        // Bez kołysania (zakres 0) – raz w jedną, raz w drugą stronę.
        if (time >= this.nextFlip) {
          const extreme = Math.floor((previous - Math.PI / 2) / Math.PI) !== Math.floor((this.swayPhase - Math.PI / 2) / Math.PI);
          let direction = 0;

          if (this.angle < 1) {
            this.flipDirection *= -1;
            direction = this.flipDirection;
          } else if (extreme) {
            direction = Math.sign(Math.sin(this.swayPhase)) || 1;
          }

          if (direction) {
            this.flip = { from: this.baseY, to: this.baseY + direction * 180, start: time };
          }
        }
      }

      if (this.flip) {
        const progress = Math.min(1, (time - this.flip.start) / this.flipDuration);

        this.baseY = this.flip.from + (this.flip.to - this.flip.from) * smootherstep(progress);

        if (progress === 1) {
          // Tyłem karta zostaje o połowę krócej – tył się "pokazuje", przód jest pozycją główną
          const showsBack = Math.cos(toRadians(this.baseY)) < 0;

          this.flip = null;
          this.nextFlip = time + this.flipPause * (showsBack ? 0.5 : 1);
        }
      }

      // Pochylenie tylko do góry i z powrotem do pionu (nigdy w dół), raz mocniej, raz słabiej; zaczyna od szczytu – tam
      // kończy się obrót przy otwarciu drawera
      const y = this.angle * (0.85 + 0.15 * Math.cos(this.rangePhase)) * Math.sin(this.swayPhase);
      const tilt = this.driftTilt * (0.85 + 0.15 * Math.cos(this.rangePhase * 1.3)) * (0.5 + 0.5 * Math.cos(this.tiltPhase));

      return { y: y * blend, tilt: tilt * blend };
    }

    // Dociąganie do celu (obrót i pochylenie) sprężyną liczoną w czasie. Prędkości są w stopniach na klatkę 60 Hz – tak
    // jak przy rzucie kartą i strzałkach.
    springTo(targetY, targetTilt, delta) {
      let speedY = this.velocityY * 60;
      let speedTilt = this.tiltVelocity * 60;

      for (let left = delta; left > 0; left -= SPRING_STEP) {
        const step = Math.min(SPRING_STEP, left) / 1000;

        speedY += (SPRING_STIFFNESS * (targetY - this.rotateY) - SPRING_DAMPING * speedY) * step;
        this.rotateY += speedY * step;
        speedTilt += (SPRING_STIFFNESS * (targetTilt - this.tilt) - SPRING_DAMPING * speedTilt) * step;
        this.tilt += speedTilt * step;
      }

      this.velocityY = speedY / 60;
      this.tiltVelocity = speedTilt / 60;
      this.tilt = clamp(this.tilt, this.maxTilt);
    }

    // Tempo 1–10: odwrócenie trwa od ok. 12 s do 1,2 s, przerwa przodem od ok. 64 s do 6,4 s (tyłem o połowę krócej)
    get flipDuration() {
      return 12000 / this.speed;
    }

    get flipPause() {
      return 64000 / this.speed;
    }

    tick(time) {
      const delta = Math.min(50, this.lastTime ? time - this.lastTime : 16.7);
      const idle = !this.dragging && this.motion !== 'none' && time - this.lastInteraction > IDLE_DELAY;

      this.frame = null;
      this.lastTime = time;

      if (this.intro) {
        this.playIntro(time);
        this.render();

        if (this.visible) {
          this.requestTick();
        } else {
          this.lastTime = 0;
        }

        return;
      }

      if (!idle) {
        // Przerwane odwracanie (dotyk, zamknięcie drawera) – karta dociąga do pozycji docelowej, nigdy nie zostaje bokiem
        if (this.flip) {
          this.baseY = this.flip.to;
        }

        this.idleStart = 0;
        this.flip = null;
      }

      if (this.dragging) {
        // Płynne podążanie za palcem (wygładzanie wykładnicze niezależne od liczby klatek na sekundę)
        const follow = 1 - Math.exp(-delta / DRAG_FOLLOW);
        const previousY = this.rotateY;

        this.rotateY += (this.dragY - this.rotateY) * follow;
        this.tilt += (this.dragTilt - this.tilt) * follow;
        this.velocityY = this.velocityY * 0.5 + ((this.rotateY - previousY) * (16.7 / delta)) * 0.5;
        this.settled = false;
      } else {
        const offset = idle ? this.idleMotion(time, delta) : { y: 0, tilt: 0 };
        const targetY = this.baseY + this.restOffsetY + offset.y;

        // Bez dotyku karta wraca do braku pochylenia; przy obrocie sama lekko się pochyla (tym mocniej, im szybciej) –
        // wygładzone, więc pochylenie nie skacze za każdą zmianą prędkości
        const motionTilt = Math.min(this.maxTilt * 0.5, Math.abs(this.velocityY) * MOTION_TILT);

        this.motionTilt = (this.motionTilt || 0) + (motionTilt - (this.motionTilt || 0)) * (1 - Math.exp(-delta / MOTION_TILT_SMOOTH));

        const targetTilt = clamp(offset.tilt + this.motionTilt, this.maxTilt);

        this.springTo(targetY, targetTilt, delta);

        this.settled = !idle && Math.abs(targetY - this.rotateY) < 0.02 && Math.abs(targetTilt - this.tilt) < 0.02 && Math.abs(this.velocityY) < 0.02 && Math.abs(this.tiltVelocity) < 0.02;
      }

      this.render();

      if (this.visible && (this.dragging || !this.settled || this.motion !== 'none')) {
        this.requestTick();
      } else {
        this.lastTime = 0;
      }
    }

    render() {
      let rotateY = this.rotateY;

      // Karta ustawiona idealnie bokiem ma zerową powierzchnię – Chrome potrafi wtedy "rozciągnąć" jej teksturę
      // na cały ekran. Omijamy więc wąski zakres ±0,75° wokół 90° / 270° (niewidoczne gołym okiem).
      const offset = (((rotateY % 180) + 180) % 180) - 90;

      if (Math.abs(offset) < 0.75) {
        rotateY += offset >= 0 ? 0.75 - offset : -0.75 - offset;
      }

      const radiansY = toRadians(rotateY);
      const radiansTilt = toRadians(this.tilt);

      // Najpierw pochylenie względem karty, potem obrót (ta sama kolejność co w maxOverflow)
      this.card.style.transform = `perspective(${Math.round(this.perspective || 2000)}px) rotateY(${rotateY.toFixed(2)}deg) rotateX(${localTilt(rotateY, this.tilt).toFixed(2)}deg)`;
      this.style.setProperty('--card-3d-shadow-strength', shadowStrength(rotateY, this.tilt).toFixed(3));
      this.style.setProperty('--card-3d-glare-x', `${(50 - Math.sin(radiansY) * 60).toFixed(1)}%`);
      this.style.setProperty('--card-3d-glare-y', `${(50 + Math.sin(radiansTilt) * 60).toFixed(1)}%`);
      this.style.setProperty('--card-3d-glare-opacity', Math.min(1, 0.15 + Math.abs(Math.sin(radiansY)) * 0.45 + Math.abs(Math.sin(radiansTilt)) * 0.3).toFixed(2));
    }
  }

  customElements.define('card-3d', Card3d);
}
